import { useCallback, useRef, useState } from 'react';
import { placesApiService } from '../services/placesApiService';

export interface AddressPrediction {
  placeId: string;
  description: string;
}

export interface AddressComponents {
  county: string;
  locality: string;
  localityType: string;
  street: string;
  streetNumber: string;
  building: string;
  entrance: string;
  floor: string;
  apartment: string;
  sector: string;
}

export const useAddressAutocomplete = () => {
  const [predictions, setPredictions] = useState<AddressPrediction[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPredictions = useCallback((input: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!input.trim()) {
      setPredictions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await placesApiService.autocomplete(input);
        setPredictions(results);
      } catch {
        setPredictions([]);
      }
    }, 300);
  }, []);

  const getAddressComponents = useCallback(
    (placeId: string) => placesApiService.getAddressComponents(placeId),
    []
  );

  const clearPredictions = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPredictions([]);
  }, []);

  return { predictions, fetchPredictions, clearPredictions, getAddressComponents };
};
